import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X, Shield, Check, Users as UsersIcon, Globe, Building, UserCircle } from 'lucide-react';

const SCOPE_ICONS = { all: Globe, agence: Building, own: UserCircle };
const SCOPE_COLORS = {
  all: 'bg-purple-100 text-purple-700',
  agence: 'bg-blue-100 text-blue-700',
  own: 'bg-gray-100 text-gray-700',
};

const emptyRole = () => ({
  name: '',
  description: '',
  scope: 'own',
  can_manage_roles: false,
  permissions: {},
});

const RolesManagement = () => {
  const [roles, setRoles] = useState([]);
  const [schema, setSchema] = useState({ modules: [], scopes: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | role_id
  const [form, setForm] = useState(emptyRole());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSchema();
    loadRoles();
  }, []);

  const loadSchema = async () => {
    try {
      const { data } = await api.get('/admin/roles/schema');
      setSchema(data);
    } catch (e) { toast.error('Erreur chargement schema'); }
  };

  const loadRoles = async () => {
    try {
      const { data } = await api.get('/admin/roles');
      setRoles(data || []);
    } catch (e) { toast.error('Erreur chargement rôles'); }
    finally { setLoading(false); }
  };

  const initForm = (role) => ({
    name: role?.name || '',
    description: role?.description || '',
    scope: role?.scope || 'own',
    can_manage_roles: !!role?.can_manage_roles,
    permissions: role?.permissions || {},
  });

  const startNew = () => {
    setEditing('new');
    const initial = {};
    schema.modules.forEach(m => {
      initial[m.key] = {};
      m.actions.forEach(a => { initial[m.key][a] = false; });
    });
    setForm({ ...emptyRole(), permissions: initial });
  };

  const startEdit = (role) => {
    setEditing(role.id);
    setForm(initForm(role));
  };

  const cancelEdit = () => { setEditing(null); setForm(emptyRole()); };

  const togglePerm = (modKey, action) => {
    setForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [modKey]: { ...(prev.permissions[modKey] || {}), [action]: !(prev.permissions[modKey]?.[action]) }
      }
    }));
  };

  const toggleModule = (modKey, value) => {
    const mod = schema.modules.find(m => m.key === modKey);
    if (!mod) return;
    setForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [modKey]: Object.fromEntries(mod.actions.map(a => [a, value]))
      }
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nom du rôle obligatoire'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/admin/roles', form);
        toast.success('Rôle créé');
      } else {
        await api.put(`/admin/roles/${editing}`, form);
        toast.success('Rôle mis à jour');
      }
      cancelEdit();
      loadRoles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally { setSaving(false); }
  };

  const handleDelete = async (role) => {
    if (role.is_system) { toast.error('Rôle système non supprimable'); return; }
    if (!window.confirm(`Supprimer le rôle "${role.name}" ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/roles/${role.id}`);
      toast.success('Rôle supprimé');
      loadRoles();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur suppression'); }
  };

  if (loading) return <div className="py-12 text-center text-[#687175]">Chargement des rôles...</div>;

  const isAllChecked = (modKey) => {
    const mod = schema.modules.find(m => m.key === modKey);
    return mod && mod.actions.every(a => form.permissions[modKey]?.[a]);
  };

  return (
    <div data-testid="roles-management">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-sm text-[#687175]">
          <Shield className="w-4 h-4" />
          {roles.length} rôle{roles.length > 1 ? 's' : ''}
        </div>
        <button onClick={startNew} disabled={!!editing}
          className="flex items-center gap-2 bg-[#397DFF] hover:bg-[#4672B6] text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          data-testid="create-role-btn">
          <Plus className="w-4 h-4" /> Nouveau rôle
        </button>
      </div>

      {/* Edit/Create panel */}
      {editing && (
        <div className="bg-white border-2 border-[#397DFF] rounded-lg p-6 mb-6" data-testid="role-edit-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-heading font-semibold text-[#191D1E]">
              {editing === 'new' ? 'Créer un rôle' : 'Modifier le rôle'}
            </h3>
            <button onClick={cancelEdit} className="text-[#687175] hover:text-[#191D1E]"><X className="w-5 h-5" /></button>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-[#191D1E] mb-1">Nom *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF]"
                placeholder="Ex: Chef de projet" data-testid="role-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#191D1E] mb-1">Périmètre de données *</label>
              <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF]"
                data-testid="role-scope-select">
                {schema.scopes.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-[#191D1E] mb-1">Description</label>
              <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF]"
                placeholder="Rôle personnalisé pour..." data-testid="role-description-input" />
            </div>
            <div className="col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.can_manage_roles}
                  onChange={(e) => setForm({ ...form, can_manage_roles: e.target.checked })}
                  className="w-4 h-4" data-testid="role-can-manage-checkbox" />
                <span className="text-sm text-[#191D1E]">Peut gérer les rôles (dans son périmètre)</span>
              </label>
            </div>
          </div>

          {/* Matrix */}
          <div className="border border-[#BFC6CC] rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#F5F6F9] border-b border-[#BFC6CC]">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#687175] uppercase">Module</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-[#687175] uppercase">Tout</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-[#687175] uppercase">Voir</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-[#687175] uppercase">Créer</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-[#687175] uppercase">Modifier</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-[#687175] uppercase">Supprimer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#BFC6CC]">
                {schema.modules.map(mod => (
                  <tr key={mod.key} className="hover:bg-[#F5F6F9]/50" data-testid={`role-perm-row-${mod.key}`}>
                    <td className="px-4 py-2.5 text-sm font-medium text-[#191D1E]">{mod.label}</td>
                    <td className="px-3 py-2.5 text-center">
                      <input type="checkbox" checked={isAllChecked(mod.key)}
                        onChange={(e) => toggleModule(mod.key, e.target.checked)}
                        className="w-4 h-4 accent-[#397DFF]"
                        data-testid={`role-perm-${mod.key}-all`} />
                    </td>
                    {['view', 'create', 'edit', 'delete'].map(action => (
                      <td key={action} className="px-3 py-2.5 text-center">
                        {mod.actions.includes(action) ? (
                          <input type="checkbox"
                            checked={!!form.permissions[mod.key]?.[action]}
                            onChange={() => togglePerm(mod.key, action)}
                            className="w-4 h-4 accent-[#397DFF]"
                            data-testid={`role-perm-${mod.key}-${action}`} />
                        ) : (
                          <span className="text-[#BFC6CC] text-xs">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button onClick={cancelEdit}
              className="px-4 py-2 border border-[#BFC6CC] text-[#687175] rounded-lg text-sm hover:bg-[#F5F6F9]">
              Annuler
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 bg-[#10D183] hover:bg-[#0BB873] text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
              data-testid="save-role-btn">
              {saving ? 'Sauvegarde...' : (editing === 'new' ? 'Créer le rôle' : 'Enregistrer')}
            </button>
          </div>
        </div>
      )}

      {/* Liste des rôles */}
      <div className="bg-white border border-[#BFC6CC] rounded-lg overflow-hidden">
        <table className="w-full" data-testid="roles-table">
          <thead className="bg-[#F5F6F9] border-b border-[#BFC6CC]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-[#687175] uppercase">Rôle</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-[#687175] uppercase">Périmètre</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-[#687175] uppercase">Permissions</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-[#687175] uppercase">Utilisateurs</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-[#687175] uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#BFC6CC]">
            {roles.map(role => {
              const ScopeIcon = SCOPE_ICONS[role.scope] || UserCircle;
              const permCount = Object.values(role.permissions || {}).reduce(
                (sum, actions) => sum + Object.values(actions).filter(Boolean).length, 0);
              const totalPerms = schema.modules.reduce((s, m) => s + m.actions.length, 0);
              return (
                <tr key={role.id} className="hover:bg-[#F5F6F9]/50" data-testid={`role-row-${role.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {role.is_system && <Shield className="w-4 h-4 text-purple-600" title="Rôle système" />}
                      <div>
                        <div className="font-medium text-[#191D1E]">{role.name}</div>
                        {role.description && <div className="text-xs text-[#687175]">{role.description}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${SCOPE_COLORS[role.scope] || ''}`}>
                      <ScopeIcon className="w-3 h-3" />
                      {schema.scopes.find(s => s.key === role.scope)?.label || role.scope}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#687175]">
                    {role.is_system ? <span className="text-purple-600 font-medium">Accès total</span> :
                      <span>{permCount} / {totalPerms}</span>}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#687175]">
                    <span className="inline-flex items-center gap-1"><UsersIcon className="w-3.5 h-3.5" />{role.users_count || 0}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => startEdit(role)} disabled={role.is_system}
                        className="p-1.5 text-[#397DFF] hover:bg-blue-50 rounded disabled:opacity-30"
                        title={role.is_system ? 'Rôle système non modifiable' : 'Modifier'}
                        data-testid={`edit-role-${role.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(role)} disabled={role.is_system}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-30"
                        title={role.is_system ? 'Rôle système' : 'Supprimer'}
                        data-testid={`delete-role-${role.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 bg-[#F5F6F9] border border-[#BFC6CC] rounded-lg p-4 text-sm text-[#687175]">
        <p className="flex items-start gap-2">
          <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#10D183]" />
          Le rôle <strong className="text-[#191D1E]">Energy Manager (Admin)</strong> dispose toujours de l'accès total et n'est pas modifiable.
          Les autres rôles sont personnalisables : choisissez les modules accessibles et les actions autorisées (voir, créer, modifier, supprimer), ainsi que le périmètre de données visible.
        </p>
      </div>
    </div>
  );
};

export default RolesManagement;
